import React, { useRef, useState } from 'react';
import { Alert, Button, Checkbox, Input, Modal, Space, Typography, message } from 'antd';
import apis from '../../apis';

export const canRecoverExpiredPayment = booking => booking.paymentStatus === 'expired'
    && booking.paymentMode === 'paper_id' && booking.ticketingVersion === 2
    && booking.seatingMode === 'numbered' && ['winner', 'public_competition'].includes(booking.bookingType)
    && booking.paymentReceipt?.status === 'needs_review'
    && booking.paymentReceipt?.reason === 'booking_canceled_before_payment';

export const expiredPaymentRecoveryRequest = (booking, note) => ({
    bookingId: booking.id, eventId: booking.eventId, invoiceId: booking.invoiceId,
    receiptId: booking.paymentReceipt.receiptId, amount: booking.totalAmount,
    seatIds: [...(booking.selectedSeatIds || [])], confirmedPaid: true, note: note.trim(),
});

const ExpiredPaymentRecovery = ({ booking, onRecovered, onBusyChange, disabled }) => {
    const [reviewBooking, setReviewBooking] = useState(null);
    const [confirmed, setConfirmed] = useState(false);
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const inFlight = useRef(false);

    const openReview = () => {
        setReviewBooking(booking);
        setConfirmed(false);
        setNote('');
        setError('');
    };
    const handleRecover = async () => {
        if (!reviewBooking || !confirmed || inFlight.current || disabled) return;
        inFlight.current = true;
        setSaving(true);
        setError('');
        onBusyChange(true);
        try {
            const result = await apis.publicTicket.reconcileExpiredPayment(expiredPaymentRecoveryRequest(reviewBooking, note));
            setReviewBooking(null);
            if (result.alreadyPaid) {
                message.info('This booking is already paid. Check Email Status before using Resend.');
            } else if (result.emailSent === true) {
                message.success('Payment reconciled and confirmation email sent.');
            } else if (result.emailSent === false) {
                message.warning('Payment reconciled. The confirmation email failed; use Resend after refreshing.');
            } else {
                message.warning('Payment reconciled. Email delivery status is uncertain; check delivery before using Resend.');
            }
        } catch (failure) {
            setError(failure.message || 'Recovery could not be confirmed. Refresh the list before trying again.');
            return;
        } finally {
            inFlight.current = false;
            setSaving(false);
            onBusyChange(false);
        }
        // A list refresh failure must not suggest that an already committed payment failed.
        try {
            await onRecovered();
        } catch (refreshError) {
            message.warning('Payment is confirmed, but the customer list could not refresh. Refresh the page to see the saved booking.');
        }
    };

    return (
        <>
            <Button size="small" onClick={openReview} disabled={disabled || saving}
                style={{ color: '#EBBC64', borderColor: '#EBBC64', backgroundColor: '#1E1E1E' }}>
                Reconcile Payment
            </Button>
            <Modal title={<span style={{ color: '#EBBC64' }}>Recover Verified Payment</span>} open={Boolean(reviewBooking)}
                styles={{ content: { backgroundColor: '#1E1E1E', color: '#e5cc92' }, header: { backgroundColor: '#1E1E1E' } }}
                onOk={handleRecover} okText="Confirm Payment and Restore Booking" confirmLoading={saving}
                okButtonProps={{ disabled: !confirmed || (disabled && !saving), style: { backgroundColor: '#EBBC64', borderColor: '#EBBC64', color: '#121212' } }}
                onCancel={() => { if (!inFlight.current) setReviewBooking(null); }}
                cancelButtonProps={{ disabled: saving }} closable={!saving} maskClosable={!saving} keyboard={!saving}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Typography.Text style={{ color: '#e5cc92' }}>
                        Buyer: {reviewBooking?.buyerName || reviewBooking?.userName || '-'}<br />
                        Booking: {reviewBooking?.id}<br />
                        Paper.id invoice ID: {reviewBooking?.invoiceId}<br />
                        Total: {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(reviewBooking?.totalAmount || 0)}<br />
                        Venue: {reviewBooking?.venueName || reviewBooking?.venue}<br />
                        Session: {reviewBooking?.date} {reviewBooking?.session} (WIB)<br />
                        Original seats: {(reviewBooking?.performanceSeatLabels || []).join(', ') || 'No numbered seats selected'}
                    </Typography.Text>
                    <Alert type="warning" showIcon style={{ backgroundColor: '#121212', borderColor: '#EBBC64' }}
                        message={<span style={{ color: '#EBBC64' }}>Verify the full payment in the production Paper.id dashboard.</span>}
                        description={<span style={{ color: '#e5cc92' }}>This action uses your verification and the saved payment receipt. It does not contact Paper.id. It restores the original tickets and available original seats together. Conflicting seats or capacity stop recovery for staff review. A successful recovery attempts the normal confirmation email.</span>} />
                    <Checkbox checked={confirmed} disabled={saving} style={{ color: '#e5cc92' }} onChange={event => setConfirmed(event.target.checked)}>
                        I verified that this exact invoice is fully paid for the matching booking and amount in Paper.id, and the payment has not been refunded.
                    </Checkbox>
                    <label htmlFor={`recovery-note-${booking.id}`}>Review note (optional)</label>
                    <Input.TextArea id={`recovery-note-${booking.id}`} value={note} maxLength={500}
                        disabled={saving} onChange={event => setNote(event.target.value)} rows={2}
                        style={{ backgroundColor: '#1E1E1E', color: '#e5cc92', borderColor: '#EBBC64' }} />
                    {error && <Alert type="error" showIcon message={error} />}
                </Space>
            </Modal>
        </>
    );
};

export default ExpiredPaymentRecovery;
